// Training and model helpers shared by the Train and Auto-Annotate pages. Ported from
// frontend/ui/training_status.py, train_model_dialog.py, auto_annotate_dialog.py and onnx_export_dialog.py;
// tests/unit/training.test.ts checks the parts that have Python originals against tests/golden/training.json.
import type { AnnotationStats, ModelVersionInfo, OnnxOptions, RecommendedThresholds, TrainingJob } from "../api/ml";

export const ACTIVE_STATUSES = ["pending", "queued", "provisioning", "running", "resuming"];
export const TERMINAL_STATUSES = ["completed", "failed", "cancelled"];
export const isActive = (status: string) => ACTIVE_STATUSES.includes(status);

/** One line for a job that has not finished (training_status.describe). */
export function describeJob(job: Pick<TrainingJob, "status" | "queue_position" | "cancel_requested"> & Pick<Partial<TrainingJob>, "device">): string {
  const s = job.status ?? "";
  if (s === "pending" || s === "queued") {
    const pos = job.queue_position;
    if (typeof pos === "number" && Number.isInteger(pos) && pos > 1) return `Waiting in the queue (${pos - 1} ahead of you)…`;
    return job.device === "gpu" ? "Waiting for a GPU training machine…" : "Waiting for a training machine…";
  }
  if (s === "provisioning") return "Starting a training machine…";
  if (s === "resuming") return "Training was interrupted and is resuming…";
  if (job.cancel_requested) return "Stopping after the current epoch…";
  return `Training ${s}…`;
}

/** Consecutive failed polls before the page says the backend is gone. */
export const POLL_FAILURES_BEFORE_WARNING = 3;
export const POLL_MS = 5000;
/** The desktop's train page and the auto-annotate loop both stop at 50 reviewed annotations. */
export const MIN_REVIEWED = 50;

export const MODEL_SIZES: [string, string][] = [
  ["n", "n  —  nano (fastest, default)"], ["s", "s  —  small"], ["m", "m  —  medium"], ["l", "l  —  large"], ["x", "x  —  extra large (most accurate, slowest)"],
];
export const IMAGE_SIZES = [320, 416, 512, 640, 768, 960, 1280];

/** "80% train / 20% val  (~40 / 10 images)" (TrainModelPage._update_split_label). */
export function splitLabel(pct: number, reviewedImages: number): string {
  const base = `${pct}% train / ${100 - pct}% val`;
  if (reviewedImages <= 0) return base;
  const train = Math.max(1, Math.trunc((reviewedImages * pct) / 100));
  return `${base}  (~${train} / ${Math.max(1, reviewedImages - train)} images)`;
}

/**
 * One line of advice under the split slider, from the reviewed images (web only; see SplitGuide). Empty when the
 * choice is sensible, so the page stays quiet in the common case.
 */
export function splitAdvice(pct: number, reviewedImages: number): string {
  if (reviewedImages <= 0) return "";
  const val = Math.max(1, reviewedImages - Math.max(1, Math.trunc((reviewedImages * pct) / 100)));
  if (val < 10) return `Only ~${val} validation image${val === 1 ? "" : "s"}: the validation score will jump around. Annotate more images if you can.`;
  if (reviewedImages < 200 && pct < 80) return `With ${reviewedImages} images, 80 – 85% for training is usually better: the model needs the examples more.`;
  if (reviewedImages > 2000 && pct < 80) return `With ${reviewedImages} images, 10 – 20% for validation is already plenty; more training data helps the model.`;
  return "";
}

/** "v8 (imported: best.pt)" rather than a bare "v8" (AutoAnnotatePage._model_description). */
export function modelDescription(version: number | null, stats: Pick<AnnotationStats, "latest_model_source">): string {
  if (!version) return "none trained yet";
  const source = stats.latest_model_source;
  return source && source !== "trained" ? `v${version} (${source})` : `v${version}`;
}

/** One line in the version picker: "v2 (latest) — imported: best.pt · 120 images", with what a person compares versions by. */
export function versionLabel(v: Pick<ModelVersionInfo, "version_number" | "is_latest" | "source" | "imported_from" | "trained_on_count" | "epochs" | "img_size" | "created_at" | "available"> & Pick<Partial<ModelVersionInfo>, "epochs_completed" | "best_map50">): string {
  const origin = v.source === "imported" ? `imported: ${v.imported_from ?? "unknown file"}` : "trained";
  const bits = [origin];
  if (v.source === "trained" && v.trained_on_count) bits.push(`${v.trained_on_count} image${v.trained_on_count === 1 ? "" : "s"}`);
  // The epochs it reached when recorded; older versions only know what was asked for.
  if (v.source === "trained" && typeof v.epochs_completed === "number") bits.push(`${v.epochs_completed} epochs`);
  else if (v.source === "trained" && v.epochs) bits.push(`${v.epochs} epochs`);
  if (typeof v.best_map50 === "number") bits.push(`mAP ${v.best_map50.toFixed(2)}`);
  if (v.img_size) bits.push(`${v.img_size}px`);
  const day = /^\d{4}-\d{2}-\d{2}/.exec(v.created_at)?.[0];
  if (day) bits.push(day);
  if (!v.available) bits.push("file missing");
  return `v${v.version_number}${v.is_latest ? " (latest)" : ""} — ${bits.join(" · ")}`;
}

/** The config line shown when a job is submitted. */
export const configLine = (p: { model_size: string; img_size: number; epochs: number | null; train_split: number }) =>
  `model=${p.model_size}, imgsz=${p.img_size}, epochs=${p.epochs ?? "auto"}, split=${Math.round(p.train_split * 100)}/${100 - Math.round(p.train_split * 100)}`;

// ── ONNX export ───────────────────────────────────────────────────────────────────────────────
export const OPSET_CHOICES = [11, 12, 13, 15, 17];
export const DEFAULT_OPSET = 12;

export const DEFAULT_ONNX: OnnxOptions = {
  target: "onnxruntime", opset: DEFAULT_OPSET, include_nms: false, conf: 0.25, iou: 0.45, max_det: 300, dynamic_batch: false, bundle: false, calib_limit: 256,
  auto_thresholds: true,
};

/** The thresholds line under the export dialog's checkbox: what the model measured, or that it measured nothing. */
export function thresholdsLine(r: RecommendedThresholds | null | undefined): string {
  if (!r) return "This version recorded no measured thresholds, so the defaults apply: confidence 0.25, NMS overlap 0.45.";
  const pct = (x: number) => `${Math.round(x * 100)}%`;
  return `Measured on this model's validation images: confidence ${r.conf.toFixed(2)}, NMS overlap ${r.iou.toFixed(2)} — finds ${pct(r.recall)} of the objects with ${pct(r.precision)} of its boxes right.`;
}

/** The Hailo and NVIDIA targets are fixed graphs: no NMS, static batch, always a bundle (_on_target_changed). */
export function applyTarget(o: OnnxOptions, target: OnnxOptions["target"]): OnnxOptions {
  const fixed = target !== "onnxruntime";
  return { ...o, target, ...(fixed ? { include_nms: false, dynamic_batch: false, bundle: true } : {}) };
}

/** What is actually sent: batch is forced off when NMS is on (ONNXExportOptionsDialog.options). */
export const effectiveOnnx = (o: OnnxOptions): OnnxOptions => ({ ...o, conf: Math.round(o.conf * 1000) / 1000, iou: Math.round(o.iou * 1000) / 1000, dynamic_batch: o.dynamic_batch && !o.include_nms });

/** The file name proposed for an export (ONNXExportFlow.start). */
export function onnxFilename(projectName: string, version: number | null, o: Pick<OnnxOptions, "target" | "bundle">): string {
  let stem = [...(projectName || "model")].map((c) => (/[\p{L}\p{N}]/u.test(c) || "._-".includes(c) ? c : "_")).join("").replace(/^[._-]+|[._-]+$/g, "") || "model";
  if (version) stem += `_v${version}`;
  if (o.target === "hailo") return `${stem}_hailo.zip`;
  if (o.target === "tensorrt") return `${stem}_nvidia.zip`;
  return o.bundle ? `${stem}_onnx.zip` : `${stem}.onnx`;
}

export const modelFilename = (version: number | null) => (version ? `best_v${version}.pt` : "best.pt");

// ── Imported model summary (AutoAnnotatePage._on_import_done) ───────────────────────────────────
export function importSummary(r: { version_number: number; classes?: string[]; model_size?: string; img_size?: number; corners?: boolean; classes_created?: string[]; classes_aliased?: Record<string, string>; warnings?: string[]; conflicts?: string[] }): string {
  const lines = [
    `Imported as model v${r.version_number}.`, "",
    `Classes:  ${(r.classes ?? []).join(", ")}`,
    `Model:    ${r.model_size} @ ${r.img_size}px`,
    `Corners:  ${r.corners ? "yes — length/breadth can be measured" : "no — detection only, no measurements"}`,
  ];
  if (r.classes_created?.length) lines.push(`Created:  ${r.classes_created.join(", ")}`);
  if (r.classes_aliased && Object.keys(r.classes_aliased).length) lines.push(`Merged:   ${Object.entries(r.classes_aliased).map(([a, b]) => `${a} → ${b}`).join(", ")}`);
  for (const note of r.warnings ?? []) lines.push("", note);
  for (const clash of r.conflicts ?? []) lines.push("", `Note: the file's recorded settings disagree with its own weights (${clash}). The weights were used.`);
  lines.push("", "Auto-Annotate will now use these weights.");
  return lines.join("\n");
}

// ── Measurement exports ────────────────────────────────────────────────────────────────────────
/** Data rows of a CSV: the number of lines less the header (main_window._export_measurements). */
export const csvRowCount = (text: string) => text.split("\n").length - 1 - 1;

/** Data rows of an .xlsx workbook, read from its first sheet without a spreadsheet library. */
export async function xlsxRowCount(blob: Blob): Promise<number> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  const dv = new DataView(buf.buffer);
  let eocd = -1;
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 70000); i--) if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  if (eocd < 0) throw new Error("not an .xlsx file");
  let p = dv.getUint32(eocd + 16, true);
  const entries = dv.getUint16(eocd + 10, true);
  const dec = new TextDecoder();
  for (let n = 0; n < entries; n++) {
    const method = dv.getUint16(p + 10, true), csize = dv.getUint32(p + 20, true);
    const nameLen = dv.getUint16(p + 28, true), extraLen = dv.getUint16(p + 30, true), commentLen = dv.getUint16(p + 32, true);
    const local = dv.getUint32(p + 42, true), name = dec.decode(buf.subarray(p + 46, p + 46 + nameLen));
    p += 46 + nameLen + extraLen + commentLen;
    if (!/^xl\/worksheets\/sheet1\.xml$/.test(name)) continue;
    const start = local + 30 + dv.getUint16(local + 26, true) + dv.getUint16(local + 28, true);
    const data = buf.subarray(start, start + csize);
    const xml = method === 0 ? dec.decode(data)
      : await new Response(new Blob([data]).stream().pipeThrough(new DecompressionStream("deflate-raw"))).text();
    return Math.max(0, (xml.match(/<row[\s>]/g) ?? []).length - 1);
  }
  throw new Error("no worksheet in the file");
}

const SIZE_NAME: Record<string, string> = { n: "nano", s: "small", m: "medium", l: "large", x: "extra-large" };

/**
 * The Train page's one-sentence account of what the Train button will do, so the defaults are visible without
 * opening Advanced: "A nano model at 640 × 640, trained long enough for your images (about 120 epochs), stopping
 * once it stops improving, on the GPU." With epochs null the server sizes the run; autoEpochs is its answer, when
 * the server gave one.
 */
export function runSummary(p: { model_size: string; img_size: number; epochs: number | null; early_stop: boolean },
  device?: "cpu" | "gpu", start?: "scratch" | number | null, autoEpochs?: number | null): string {
  const name = SIZE_NAME[p.model_size] ?? p.model_size;
  const length = p.epochs === null
    ? `trained long enough for your images${autoEpochs ? ` (about ${autoEpochs} epochs)` : ""}`
    : `up to ${p.epochs} epochs`;
  const bits = [`A ${name} model at ${p.img_size} × ${p.img_size}`, length];
  if (p.early_stop) bits.push("stopping once it stops improving");
  if (device) bits.push(`on the ${device.toUpperCase()}`);
  if (start === "scratch") bits.push("from scratch");
  else if (typeof start === "number") bits.push(`continuing from v${start}`);
  return `${bits.join(", ")}.`;
}
